import { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import { appToast } from '../../../../../components/AppToast'
import AppRichTextEditor from '../../../../../components/AppRichTextEditor'
import { sendBatchEmail } from '../../api/studentsApi'

const EMAIL_FROM_OPTIONS = [
  { value: 'admin@institution.edu', label: 'admin@institution.edu (Admin Office)' },
  { value: 'noreply@institution.edu', label: 'noreply@institution.edu (No Reply)' },
  { value: 'exams@institution.edu', label: 'exams@institution.edu (Examination Dept)' },
  { value: 'accounts@institution.edu', label: 'accounts@institution.edu (Accounts)' },
]

const MAIL_MERGE_TAGS = [
  { tag: '#FirstName#', desc: 'First Name of the student' },
  { tag: '#Student#', desc: 'Full Name of the student' },
  { tag: '#CourseName#', desc: 'Course Name of the student' },
  { tag: '#SchemeName#', desc: 'Scheme Name of the student' },
  { tag: '#Guardian#', desc: 'Name of the parent/guardian addressed' },
  { tag: '#Relation#', desc: 'Relation of guardian with student' },
  { tag: '#InstituteName#', desc: 'Name of the Institute' },
]

const EMAIL_TEMPLATES = [
  {
    label: 'Welcome',
    subject: 'Welcome to Our Institution',
    bodyHtml:
      '<p>Dear Student,</p><p>Welcome to our institution! We are delighted to have you join us. Please find your enrollment details attached.</p><p>If you have any questions, feel free to reach out.</p><p>Best Regards,<br/>Admin Office</p>',
  },
  {
    label: 'Fee Reminder',
    subject: 'Fee Payment Reminder',
    bodyHtml:
      '<p>Dear Student,</p><p>This is a friendly reminder that your fee payment is due.</p><p>Please ensure timely payment to avoid late charges.</p><p>Best Regards,<br/>Accounts Department</p>',
  },
  {
    label: 'Exam Schedule',
    subject: 'Examination Schedule',
    bodyHtml:
      '<p>Dear Student,</p><p>Please find the examination schedule below. Ensure you carry your ID card and admit card.</p><p>All the best!</p><p>Best Regards,<br/>Examination Department</p>',
  },
  {
    label: 'General Notice',
    subject: 'Important Notice',
    bodyHtml:
      '<p>Dear Student,</p><p>This is to inform you about an important update. Please read the details carefully.</p><p>Best Regards,<br/>Administration</p>',
  },
]

const DEFAULT_BODY = `<p>Dear Student,</p><p>Welcome to our institution! We are delighted to have you join us. Please find your enrollment details attached.</p><p>If you have any questions, feel free to reach out.</p><p>Best Regards,<br/>Admin Office</p>`

export default function SendEmailModal({ isOpen, onClose, students = [] }) {
  const [sendToStudent, setSendToStudent] = useState(true)
  const [sendToGuardian1, setSendToGuardian1] = useState(true)
  const [sendToGuardian2, setSendToGuardian2] = useState(true)
  const [emailFrom, setEmailFrom] = useState(EMAIL_FROM_OPTIONS[0].value)
  const [subject, setSubject] = useState('Welcome to Our Institution')
  const [emailBody, setEmailBody] = useState(DEFAULT_BODY)
  const [sending, setSending] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const editorRef = useRef(null)

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen && !emailBody) {
      setEmailBody(DEFAULT_BODY)
    }
  }, [isOpen, emailBody])

  const recipients = useMemo(() => {
    const list = []
    students.forEach((s) => {
      if (!s.email) return
      if (sendToStudent) list.push({ name: `${s.firstName || ''} ${s.lastName || ''}`.trim(), email: s.email, role: 'Student' })
      if (sendToGuardian1 && s.guardianName) list.push({ name: s.guardianName, email: s.email, role: 'Guardian 1' })
      if (sendToGuardian2 && s.guardianName) list.push({ name: s.guardianName, email: s.email, role: 'Guardian 2' })
    })
    return list
  }, [students, sendToStudent, sendToGuardian1, sendToGuardian2])

  const insertMergeTag = useCallback((tag) => {
    if (editorRef.current?.insertTag) {
      editorRef.current.insertTag(tag)
    } else {
      setEmailBody((prev) => `${prev} ${tag}`)
    }
    appToast.info(`Inserted ${tag}`)
  }, [])

  const handleTemplateSelect = useCallback((tpl) => {
    setSubject(tpl.subject)
    setEmailBody(tpl.bodyHtml)
    if (editorRef.current?.setHtml) {
      editorRef.current.setHtml(tpl.bodyHtml)
    }
    setShowTemplates(false)
  }, [])

  const handlePreview = useCallback(() => {
    const currentBody = editorRef.current?.getHtml?.() || emailBody
    if (!currentBody.trim()) {
      appToast.warning('Enter email body to preview.')
      return
    }
    const win = window.open('about:blank', '_blank', 'width=700,height=600')
    if (!win) return
    win.document.open()
    win.document.write(
      `<!doctype html><html><head><meta charset="utf-8" /><title>Email Preview</title>
      <style>
        body{font-family:Arial,sans-serif;margin:0;padding:32px;background:#f5f5f5;color:#191c1e;}
        .card{max-width:600px;margin:0 auto;background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,.08);overflow:hidden;}
        .meta{padding:16px 24px;background:#f8f9fa;border-bottom:1px solid #eee;font-size:12px;color:#666;}
        .meta p{margin:2px 0;}
        .body{padding:24px;font-size:14px;line-height:1.6;}
      </style></head><body>
      <div class="card">
        <div class="meta">
          <p><strong>From:</strong> ${emailFrom}</p>
          <p><strong>To:</strong> ${recipients.length} recipient(s)</p>
          <p><strong>Subject:</strong> ${subject}</p>
        </div>
        <div class="body">${currentBody}</div>
      </div>
      </body></html>`,
    )
    win.document.close()
  }, [emailFrom, subject, recipients, emailBody])

  const handleSend = async () => {
    const currentBody = editorRef.current?.getHtml?.() || emailBody
    if (!subject.trim()) {
      appToast.warning('Please enter an email subject.')
      return
    }
    if (!currentBody.trim()) {
      appToast.warning('Please enter an email body.')
      return
    }
    if (recipients.length === 0) {
      appToast.warning('No recipients selected with valid email addresses.')
      return
    }

    setSending(true)
    try {
      const studentIds = students.map((s) => s.id)
      const res = await sendBatchEmail({
        studentIds,
        subject: subject.trim(),
        bodyHtml: currentBody.trim(),
        emailFrom,
        sendToStudent,
        sendToGuardian1,
        sendToGuardian2,
      })
      appToast.success(res?.message || `Email sent successfully to ${res?.affected_count || recipients.length} recipient(s).`)
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to send batch email.')
    } finally {
      setSending(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[92vh] max-h-[850px] w-full max-w-[1140px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-on-surface">Send Email</h3>
            <p className="text-xs text-on-surface-variant">
              {recipients.length} recipient(s) &middot; Bulk email from current filter
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-on-surface-variant transition-colors hover:bg-surface-container-high hover:text-on-surface cursor-pointer"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* ── Two-Column Body ────────────────────────────────────────────── */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* ── Left: Email Composer ─────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-4">
              {/* Send To */}
              <div className="flex items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Send To:</label>
                <div className="flex items-center gap-5">
                  {[
                    { label: 'Students', checked: sendToStudent, set: setSendToStudent },
                    { label: 'Guardian 1', checked: sendToGuardian1, set: setSendToGuardian1 },
                    { label: 'Guardian 2', checked: sendToGuardian2, set: setSendToGuardian2 },
                  ].map((opt) => (
                    <label key={opt.label} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={opt.checked}
                        onChange={(e) => opt.set(e.target.checked)}
                        className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20"
                      />
                      <span className="text-sm text-on-surface">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Email From */}
              <div className="flex items-center gap-3">
                <label className="shrink-0 text-sm font-bold text-on-surface">Email From:</label>
                <select
                  value={emailFrom}
                  onChange={(e) => setEmailFrom(e.target.value)}
                  className="flex-1 rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2 text-sm font-medium text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {EMAIL_FROM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div>
                <label className="mb-1.5 block text-sm font-bold text-on-surface">Subject:</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2.5 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Enter email subject..."
                />
              </div>

              {/* Standard Rich Text Editor */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label className="block text-sm font-bold text-on-surface">Body:</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTemplates(!showTemplates)}
                      className="text-xs font-bold text-primary hover:underline cursor-pointer"
                    >
                      {showTemplates ? 'Hide Templates' : 'Select Template'}
                    </button>
                  </div>
                </div>

                {/* Template Dropdown (collapsible) */}
                {showTemplates && (
                  <div className="mb-3 rounded-xl border border-outline-variant/30 bg-surface-container-low p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wider text-on-surface-variant">
                      Predefined Email Templates
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {EMAIL_TEMPLATES.map((tpl) => (
                        <button
                          key={tpl.label}
                          type="button"
                          onClick={() => handleTemplateSelect(tpl)}
                          className="flex items-center gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-lowest p-2.5 text-left transition-colors hover:bg-primary/5 hover:border-primary/40 cursor-pointer"
                        >
                          <span className="material-symbols-outlined text-base text-primary">description</span>
                          <div>
                            <p className="text-xs font-semibold text-on-surface">{tpl.label}</p>
                            <p className="text-[11px] text-on-surface-variant truncate max-w-[200px]">{tpl.subject}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <AppRichTextEditor
                  ref={editorRef}
                  value={emailBody}
                  onChange={setEmailBody}
                  height={260}
                  placeholder="Compose email body or select a template above..."
                />
              </div>
            </div>
          </div>

          {/* ── Divider ──────────────────────────────────────────────────── */}
          <div className="w-px bg-outline-variant/20" />

          {/* ── Right: TIPS Card ─────────────────────────────────────────── */}
          <div className="flex w-[320px] shrink-0 flex-col overflow-y-auto bg-surface-container-low px-5 py-5">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider text-on-surface">
              <span className="material-symbols-outlined text-lg text-primary">tips_and_updates</span>
              TIPS
            </h4>

            <div className="space-y-3 text-xs leading-relaxed text-on-surface-variant">
              <ul className="space-y-2">
                {[
                  'You can choose to send Email to Students, Father (First Guardian), Mother (Second Guardian).',
                  'Enter Subject and Mail body for sending mail.',
                  'You can enable or disable Email sending for each email ID from student module.',
                  'Email does not allow attachments, but you can upload a file and embed the link to that file within your email.',
                  'You can format email with bold, colors, tables, and hyperlinks.',
                ].map((tip, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="material-symbols-outlined mt-0.5 shrink-0 text-[14px] text-primary">check_circle</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>

              {/* Mail-Merge Tags */}
              <div className="mt-4">
                <p className="mb-2 text-xs font-black uppercase tracking-wider text-on-surface">
                  Mail-Merge Dynamic Tags
                </p>
                <p className="mb-2 text-[11px] text-on-surface-variant">
                  Click any tag to insert it into your email body:
                </p>
                <div className="space-y-1">
                  {MAIL_MERGE_TAGS.map((item) => (
                    <button
                      key={item.tag}
                      type="button"
                      onClick={() => insertMergeTag(item.tag)}
                      className="group flex w-full items-start gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors hover:bg-primary/5 cursor-pointer"
                      title={`Click to insert ${item.tag}`}
                    >
                      <code className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-bold text-primary">
                        {item.tag}
                      </code>
                      <span className="text-[11px] text-on-surface-variant group-hover:text-on-surface">
                        {item.desc}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/20 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-5 py-2.5 text-sm font-bold text-on-surface-variant transition-colors hover:bg-surface-container-high cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePreview}
            className="rounded-xl border border-outline-variant/40 bg-surface-container-low px-5 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-high cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <span className="material-symbols-outlined text-base">visibility</span>
              Preview
            </span>
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !subject.trim()}
            className="rounded-xl bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-sm transition-all hover:shadow-md disabled:opacity-60 cursor-pointer"
          >
            {sending ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">mail</span>
                Send
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
